import {
  ClipboardDocumentListIcon,
  PhoneArrowUpRightIcon,
} from '@heroicons/react/24/outline';
import * as React from 'react';
import { UseFormReturn } from 'react-hook-form';

import { TaskLink } from '@/components/entity-links';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { GroupedCombobox } from '@/components/ui/grouped-combobox';
import { Input } from '@/components/ui/input';
import {
  CarrierContactWithTask,
  useCarrierTasksForHoldTransfer,
} from '@/hooks/useCarrierTasksForHoldTransfer';
import { formatCoverageCategory } from '@/hooks/useInsuranceContacts';
import { useGetUserTransferPhoneQuery } from '@/services/api/aiHoldAndTransferService';
import { isPhoneNumberValid } from '@/utils/validation';

// Format phone number for display
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  // Handle E.164 format
  if (phoneNumber.startsWith('+')) {
    const match = phoneNumber.match(/^\+(\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
    }
  }

  // Handle standard US formats
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return phoneNumber;
};

interface AIHoldAndTransferFormData {
  contact: CarrierContactWithTask | null;
  phoneNumber: string;
  taskId: string | null;
  transferToPhone: string;
}

interface AIHoldAndTransferContactSelectorProps {
  form: UseFormReturn<AIHoldAndTransferFormData>;
  matterId: string;
}

export const AIHoldAndTransferContactSelector: React.FC<
  AIHoldAndTransferContactSelectorProps
> = ({ form, matterId }) => {
  const { carrierContacts, adjusterContacts, isLoading, taskCount } =
    useCarrierTasksForHoldTransfer(matterId);

  // Bug #3: Auto-populate user's Dialpad number when available
  const { data: userTransferPhone } = useGetUserTransferPhoneQuery();
  React.useEffect(() => {
    const existing = form.getValues('transferToPhone');
    if (userTransferPhone?.phone_number && !existing) {
      form.setValue('transferToPhone', userTransferPhone.phone_number);
    }
  }, [userTransferPhone, form]);

  const selectedContact = form.watch('contact');

  const getContactDisplayValue = (contact: CarrierContactWithTask) => {
    const categoryLabel = formatCoverageCategory(contact.coverageCategory);
    const carrierPart = contact.carrierName || contact.name;
    const parts = [carrierPart];
    if (categoryLabel) parts.push(`(${categoryLabel})`);
    if (contact.secondaryTitle && contact.secondaryTitle !== contact.name) {
      parts.push(`- ${contact.secondaryTitle}`);
    }
    return parts.join(' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin h-4 w-4 border-2 border-violet-600 border-t-transparent rounded-full" />
        <span className="ml-2 text-[11px] text-zinc-500">
          Loading insurance tasks...
        </span>
      </div>
    );
  }

  if (taskCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-4 text-center">
        <PhoneArrowUpRightIcon className="h-8 w-8 text-zinc-300 mb-2" />
        <p className="text-[11px] font-medium text-zinc-600">
          No insurance tasks with coverages found
        </p>
        <p className="text-[10px] text-zinc-500 mt-0.5">
          AI Hold & Transfer requires a task with carrier info
        </p>
      </div>
    );
  }

  const hasContacts = carrierContacts.length > 0 || adjusterContacts.length > 0;

  if (!hasContacts) {
    return (
      <div className="flex flex-col items-center justify-center py-4 text-center">
        <PhoneArrowUpRightIcon className="h-8 w-8 text-zinc-300 mb-2" />
        <p className="text-[11px] font-medium text-zinc-600">
          No carrier contacts found
        </p>
        <p className="text-[10px] text-zinc-500 mt-0.5">
          Found {taskCount} task(s) with coverage, but no carrier phone numbers
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {/* Info banner */}
      <div className="flex items-start gap-2 px-2.5 py-2 bg-violet-50 border border-violet-200 rounded">
        <PhoneArrowUpRightIcon className="h-4 w-4 text-violet-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-[10px] font-semibold text-violet-800">
            AI Hold & Transfer
          </p>
          <p className="text-[9px] text-violet-700 mt-0.5">
            AI will call the carrier, navigate menus, and wait on hold. When a
            human answers, you&apos;ll get a Slack notification and the call
            transfers to your phone.
          </p>
        </div>
      </div>

      <FormField
        control={form.control}
        name="contact"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className="text-[10px] font-medium text-zinc-800">
              Select Carrier Contact
            </FormLabel>
            <FormControl>
              <GroupedCombobox
                searchKeys={[
                  'name',
                  'carrierName',
                  'phone',
                  'secondaryTitle',
                  'taskName',
                  'taskTypeName',
                ]}
                label=""
                groups={[
                  ...(adjusterContacts.length > 0
                    ? [
                        {
                          label: 'Adjusters & Subrogators',
                          options: adjusterContacts,
                        },
                      ]
                    : []),
                  ...(carrierContacts.length > 0
                    ? [
                        {
                          label: 'Carrier Lines',
                          options: carrierContacts,
                        },
                      ]
                    : []),
                ]}
                value={field.value}
                onChange={(value) => {
                  if (value) {
                    field.onChange(value);
                    form.setValue('phoneNumber', value.phone);
                    form.setValue('taskId', value.taskId);
                  }
                }}
                placeholder="Select a carrier contact..."
                displayValue={getContactDisplayValue}
                renderItem={({ item: contact, isSelected }) => {
                  const categoryLabel = formatCoverageCategory(
                    contact.coverageCategory,
                  );
                  return (
                    <div
                      className={`flex flex-col w-full min-w-0 py-1.5 px-1 rounded-sm ${
                        isSelected ? 'bg-zinc-100' : ''
                      }`}
                    >
                      {/* Carrier name and category */}
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-[11px] text-zinc-900 truncate text-left">
                          {contact.carrierName || contact.name}
                        </span>
                        {categoryLabel && (
                          <span className="text-[8px] font-semibold text-violet-600 bg-violet-100 px-1 py-0.5 rounded flex-shrink-0">
                            {categoryLabel}
                          </span>
                        )}
                      </div>

                      {/* Contact details */}
                      <div className="flex items-center gap-1 text-[9px] text-left mt-0.5">
                        {contact.secondaryTitle && (
                          <span className="font-medium text-zinc-600">
                            {contact.secondaryTitle}
                          </span>
                        )}
                        <span className="text-zinc-400">•</span>
                        <span className="font-medium text-zinc-700">
                          {formatPhoneNumber(contact.phone)}
                        </span>
                      </div>

                      {/* Task association - highlighted */}
                      <div className="flex items-center gap-1 mt-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded">
                        <ClipboardDocumentListIcon className="h-3 w-3 text-amber-600 flex-shrink-0" />
                        <span className="text-[8px] font-medium text-amber-700 truncate">
                          Links to: {contact.taskTypeName || contact.taskName}
                        </span>
                      </div>
                    </div>
                  );
                }}
                renderSelection={({ item: contact }) =>
                  field.value ? (
                    <div className="grid grid-cols-[1fr_auto] gap-1.5 items-center w-full py-0.5 min-w-0">
                      <div className="flex flex-col w-full min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-[11px] text-zinc-900 truncate text-left">
                            {contact.carrierName || contact.name}
                          </span>
                          {formatCoverageCategory(contact.coverageCategory) && (
                            <span className="text-[8px] font-semibold text-violet-600 bg-violet-100 px-1 py-0.5 rounded flex-shrink-0">
                              {formatCoverageCategory(contact.coverageCategory)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-[9px] text-left w-full">
                          {contact.secondaryTitle && (
                            <span className="font-medium text-zinc-600 truncate">
                              {contact.secondaryTitle}
                            </span>
                          )}
                          <span className="font-medium text-zinc-700 truncate">
                            {formatPhoneNumber(contact.phone)}
                          </span>
                        </div>
                      </div>
                      <div
                        role="button"
                        tabIndex={0}
                        className="text-zinc-500 hover:text-zinc-800 cursor-pointer text-[9px] font-medium px-1 py-0.5 rounded hover:bg-zinc-100 transition-colors shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          field.onChange(null);
                          form.setValue('phoneNumber', '');
                          form.setValue('taskId', null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            field.onChange(null);
                            form.setValue('phoneNumber', '');
                            form.setValue('taskId', null);
                          }
                        }}
                      >
                        Clear
                      </div>
                    </div>
                  ) : (
                    <span className="text-zinc-500 font-medium text-[10px] truncate">
                      Select a carrier contact...
                    </span>
                  )
                }
              />
            </FormControl>
            <FormMessage className="text-[9px]" />
          </FormItem>
        )}
      />

      {/* Phone number to call (carrier) */}
      <FormField
        control={form.control}
        name="phoneNumber"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className="text-[10px] font-medium text-zinc-800">
              Phone Number to Call {selectedContact && '(override)'}
            </FormLabel>
            <FormControl>
              <Input
                type="tel"
                placeholder="e.g. (303) 217-0796"
                className="h-7 text-[11px] font-medium transition-colors data-[invalid=true]:border-red-300 data-[invalid=true]:ring-red-100"
                data-invalid={field.value && !isPhoneNumberValid(field.value)}
                {...field}
              />
            </FormControl>
            {field.value && !isPhoneNumberValid(field.value) && (
              <p className="text-[9px] font-medium text-red-600">
                Enter a valid phone number
              </p>
            )}
          </FormItem>
        )}
      />

      {/* Transfer phone number (where user receives the call) */}
      <FormField
        control={form.control}
        name="transferToPhone"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className="text-[10px] font-medium text-zinc-800">
              Your Phone Number{' '}
              <span className="font-normal text-zinc-500">(optional)</span>
            </FormLabel>
            <FormControl>
              <Input
                type="tel"
                placeholder="Leave blank to use your RingCentral number"
                className="h-7 text-[11px] font-medium transition-colors data-[invalid=true]:border-red-300 data-[invalid=true]:ring-red-100"
                data-invalid={
                  field.value &&
                  field.value.length > 0 &&
                  !isPhoneNumberValid(field.value)
                }
                {...field}
              />
            </FormControl>
            <p className="text-[9px] text-zinc-500">
              The call will transfer to this number when a human answers
            </p>
            {field.value &&
              field.value.length > 0 &&
              !isPhoneNumberValid(field.value) && (
                <p className="text-[9px] font-medium text-red-600">
                  Enter a valid phone number
                </p>
              )}
          </FormItem>
        )}
      />

      {/* Task association confirmation - shown when contact is selected */}
      {selectedContact && (
        <div className="flex items-start gap-2 px-2.5 py-2 bg-amber-50 border border-amber-200 rounded">
          <ClipboardDocumentListIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold text-amber-800">
              Call will be linked to task
            </p>
            <div className="text-[9px] text-amber-700 mt-0.5">
              <TaskLink
                entityId={selectedContact.taskId}
                matterId={matterId}
                className="font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2"
              >
                {selectedContact.taskTypeName || selectedContact.taskName}
              </TaskLink>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
